import { Config, Forms } from '@clearkit/icons';
import { CKTab, CKTabList, CKTabPanel, CKTabPanels } from 'clearkit';

import { routes } from '~/components/app/appMainRoutes';
import AccountConnectorAction from '~/components/layouts/ConnectorLayout/AccountConnectorAction';
import { useAccountConnector } from '~/components/layouts/ConnectorLayout/useAccountConnector';
import { PageLayoutTabs } from '~/components/PageLayout';
import { useRouterTabs } from '~/components/unified/hooks';
import { useEntitlements } from '~/hooks/account';
import {
  Configuration as ConfigurationType,
  configurationNameForType,
} from '~/lib/configuration';

import Configuration from './Configuration';
import FormFieldMappings from './Mappings';

export type FormConfigurationProps = {
  integration: ConfigurationType;
  selectedTab?: string;
};

export function FormsPage({
  integration,
  selectedTab = 'configuration',
}: FormConfigurationProps) {
  const formsRoutes = {
    0: `/forms/${integration}/configuration`,
    1: `/forms/${integration}/mappings`,
  };

  const selectedTabIndex = selectedTab === 'mappings' ? 1 : 0;

  const { handleTabIndexChange, tabIndex } = useRouterTabs(
    formsRoutes,
    selectedTabIndex || 0,
  );

  const { accountConnector, isAccountConnectorComplete } = useAccountConnector(
    integration,
  );

  const { hasAccessIntegrations } = useEntitlements();

  const integrationDisplayName = configurationNameForType(integration);
  const title = `${integrationDisplayName} Form Complete`;

  return (
    <PageLayoutTabs index={tabIndex} onChange={handleTabIndexChange}>
      <PageLayoutTabs.Header>
        <PageLayoutTabs.Tile variant="purple">
          <Forms />
        </PageLayoutTabs.Tile>

        {hasAccessIntegrations ? (
          <PageLayoutTabs.Breadcrumb
            breadcrumbs={[
              {
                title: 'Integrations',
                path: routes.integrations,
              },
            ]}
          >
            {title}
          </PageLayoutTabs.Breadcrumb>
        ) : (
          <PageLayoutTabs.Heading>{title}</PageLayoutTabs.Heading>
        )}

        <PageLayoutTabs.SubHeading>
          Improve conversion rates on {integrationDisplayName} forms
        </PageLayoutTabs.SubHeading>

        <PageLayoutTabs.Actions>
          {isAccountConnectorComplete ? (
            <AccountConnectorAction accountConnector={accountConnector} />
          ) : null}
        </PageLayoutTabs.Actions>
        <CKTabList>
          <CKTab icon={<Config height={16} width={16} />} label="Configuration">
            Configuration
          </CKTab>
          <CKTab
            icon={<Forms height={16} width={16} />}
            label="Form Field Mappings"
          >
            Form Field Mappings
          </CKTab>
        </CKTabList>
      </PageLayoutTabs.Header>
      <PageLayoutTabs.Body>
        <PageLayoutTabs.Content>
          <CKTabPanels>
            <CKTabPanel>
              <Configuration integration={integration} />
            </CKTabPanel>
            <CKTabPanel>
              <FormFieldMappings integration={integration} />
            </CKTabPanel>
          </CKTabPanels>
        </PageLayoutTabs.Content>
      </PageLayoutTabs.Body>
    </PageLayoutTabs>
  );
}
